import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import useFirstRender from '@swf/core/hooks/useFirstRender';
import { select } from '@swf/core/store';

const assetPath = '/assets/js/bootstrap.min.js';
const contextPath = '/assets/htdocs/sitecontext.html';

const SalesforceLoader: React.FC = () => {
  const locale = useSelector(select.locale.get);
  const { idpToken } = useSelector(select.session.get);
  const userIntId = useSelector(select.account.getUserIntId);
  const licence = useSelector(select.cms.getSiteName);
  const isSFAuth = useSelector(select.help.isSFAuthenticated);
  const { salesforceSrc, salesforceScrt, salesforceDeploymentCode, salesforceDeploymentName } =
    useSelector(select.help.getSFChatInfo);
  const {
    salesforceSrcUnauthenticated: warmupSalesforceSrc,
    salesforceSrcAuthenticated: authSalesforceSrc,
    salesforceScrt: warmupSalesforceScrt,
    salesforceDeploymentCode: warmupSalesforceDeploymentCode,
    salesforceDeploymentNameUnauthenticated: warmupSalesforceDeploymentName
  } = useSelector(select.config.get);
  const isFirstRender = useFirstRender();

  const setEmbeddedMessagingReady = () => {
    if (!window.embeddedservice_bootstrap) {
      return;
    }
    if (idpToken && userIntId) {
      window.embeddedservice_bootstrap.userVerificationAPI.setIdentityToken({
        identityTokenType: 'JWT',
        identityToken: idpToken
      });
      window.embeddedservice_bootstrap.prechatAPI?.setHiddenPrechatFields({
        PlayerID: userIntId.toString(),
        Language: locale,
        LicenceType: licence
      });
    } else {
      window.embeddedservice_bootstrap.prechatAPI?.setHiddenPrechatFields({
        Language: locale,
        LicenceType: licence
      });
    }
  };

  const initEmbeddedMessaging = () => {
    if (
      !salesforceDeploymentCode ||
      !salesforceDeploymentName ||
      !salesforceSrc ||
      !salesforceScrt
    ) {
      return;
    }

    if (window.embeddedservice_bootstrap) {
      window.embeddedservice_bootstrap.settings.language = locale;
      window.embeddedservice_bootstrap.settings.restrictSessionOnMessagingChannel = true;
      window.embeddedservice_bootstrap.settings.shouldMinimizeWindowOnNewTab = true;
    }
    window.addEventListener('onEmbeddedMessagingReady', setEmbeddedMessagingReady);
    window.embeddedservice_bootstrap?.init(
      salesforceDeploymentCode,
      salesforceDeploymentName,
      salesforceSrc,
      { scrt2URL: salesforceScrt }
    );
  };

  const createMIAWScript = () => {
    if (!salesforceSrc) {
      return;
    }

    const script = document.createElement('script');
    script.id = 'SFScript';
    script.src = salesforceSrc.concat(assetPath);
    script.type = 'text/javascript';
    script.onload = initEmbeddedMessaging;
    script.async = true;

    document.body.appendChild(script);
  };

  const cleanSharedStorage = () => {
    const iframe = document.getElementById('siteContextFrameUnAuth') as HTMLIFrameElement;
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(
        {
          method: 'ESW_3RDPARTY_STORAGE_CLEAR',
          data: salesforceDeploymentCode
        },
        '*'
      );
    }
  };

  const cleanDOM = () => {
    window.removeEventListener('onEmbeddedMessagingReady', warmupDone);
    window.removeEventListener('onEmbeddedMessagingReady', setEmbeddedMessagingReady);
    window.embeddedservice_bootstrap?.removeEventHandlers();
    const inert = document.getElementById('inert-script');
    const embedded = document.getElementById('embedded-messaging');
    const frame = document.getElementById('embeddedMessagingSiteContextFrame');
    const SFScript = document.getElementById('SFScript');
    const SFScriptW = document.getElementById('SFScriptW');
    const preview = document.getElementById('embeddedMessagingFilePreviewFrame');
    if (inert) document.body.removeChild(inert);
    if (embedded) document.body.removeChild(embedded);
    if (frame) document.body.removeChild(frame);
    if (SFScript) document.body.removeChild(SFScript);
    if (SFScriptW) document.body.removeChild(SFScriptW);
    if (preview) document.body.removeChild(preview);
    if (salesforceDeploymentCode) {
      localStorage.removeItem(salesforceDeploymentCode.concat('_WEB_STORAGE'));
      sessionStorage.removeItem(salesforceDeploymentCode.concat('_WEB_STORAGE'));
    }
  };

  const warmupDone = () => {
    if (!window.embeddedservice_bootstrap) {
      return;
    }
    window.embeddedservice_bootstrap.userVerificationAPI.clearSession().then(() => {
      cleanSharedStorage();
      cleanDOM();
      createMIAWScript();
    });
  };

  const initWarmup = () => {
    if (
      !warmupSalesforceDeploymentCode ||
      !warmupSalesforceDeploymentName ||
      !warmupSalesforceSrc ||
      !warmupSalesforceScrt
    ) {
      return;
    }

    if (window.embeddedservice_bootstrap) {
      window.embeddedservice_bootstrap.settings.language = locale;
      window.embeddedservice_bootstrap.settings.restrictSessionOnMessagingChannel = true;
      window.embeddedservice_bootstrap.settings.shouldMinimizeWindowOnNewTab = true;
      window.embeddedservice_bootstrap.settings.hideChatButtonOnLoad = true;
    }
    window.addEventListener('onEmbeddedMessagingReady', warmupDone, { once: true });
    window.embeddedservice_bootstrap?.init(
      warmupSalesforceDeploymentCode,
      warmupSalesforceDeploymentName,
      warmupSalesforceSrc,
      { scrt2URL: warmupSalesforceScrt }
    );
  };

  const warmup = () => {
    if (!warmupSalesforceSrc) {
      return;
    }

    const script = document.createElement('script');
    script.id = 'SFScriptW';
    script.src = warmupSalesforceSrc.concat(assetPath);
    script.type = 'text/javascript';
    script.onload = initWarmup;
    script.async = true;

    document.body.appendChild(script);
  };

  useEffect(() => {
    if (!salesforceDeploymentCode) {
      return;
    }
    cleanDOM();
    warmup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSFAuth]);

  useEffect(() => {
    if (idpToken && !isFirstRender) {
      window.embeddedservice_bootstrap?.userVerificationAPI?.setIdentityToken({
        identityTokenType: 'JWT',
        identityToken: idpToken
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idpToken]);

  // effect only runs once and the effect cleanup will run on component unmount
  useEffect(
    () => () => {
      if (window.embeddedservice_bootstrap?.userVerificationAPI?.clearSession) {
        window.embeddedservice_bootstrap.userVerificationAPI.clearSession().finally(() => {
          cleanDOM();
        });
      } else {
        cleanDOM();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div hidden>
      <iframe
        title={'UnauthIframe'}
        id="siteContextFrameUnAuth"
        src={(warmupSalesforceSrc ? warmupSalesforceSrc : '').concat(contextPath)}
      />
      <iframe
        title={'AuthIframe'}
        id="siteContextFrameAuth"
        src={(authSalesforceSrc ? authSalesforceSrc : '').concat(contextPath)}
      />
    </div>
  );
};

export default SalesforceLoader;
